import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { CHECKLIST_SECTIONS, SABATINI_SECTION } from "@/lib/checklist";
import { toast } from "sonner";
import { Upload, CheckCircle2, Circle, Download, Trash2, FileText, Copy, ArrowLeft, ClipboardList, Landmark } from "lucide-react";
import { motion } from "framer-motion";

export default function Checklist() {
  const { dossierId } = useParams();
  const nav = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("iper");

  const load = async () => {
    try {
      const { data } = await api.get(`/dossier/${dossierId}`);
      setDossier(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setDossier(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dossierId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const docsByItem = (itemKey) => (dossier?.documents || []).filter((d) => d.item_key === itemKey);
  const iperSections = CHECKLIST_SECTIONS;
  const sabatiniSections = [SABATINI_SECTION];
  const visibleSections = activeTab === "sabatini" ? sabatiniSections : iperSections;

  const countCompleted = (secs) => secs.reduce((s, sec) => s + sec.items.filter((it) => docsByItem(it.key).length > 0).length, 0);
  const countTotal = (secs) => secs.reduce((s, sec) => s + sec.items.length, 0);
  const iperCompleted = countCompleted(iperSections);
  const iperTotal = countTotal(iperSections);
  const sabCompleted = countCompleted(sabatiniSections);
  const sabTotal = countTotal(sabatiniSections);

  const completedItems = activeTab === "sabatini" ? sabCompleted : iperCompleted;
  const totalItems = activeTab === "sabatini" ? sabTotal : iperTotal;
  const totalDocs = dossier?.documents?.length || 0;
  const progress = totalItems ? Math.round((completedItems / totalItems) * 100) : 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link della check list copiato");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto p-10 text-ink-muted">Caricamento...</div>;
  if (!dossier) return (
    <div className="max-w-3xl mx-auto p-10 text-center">
      <p className="text-ink-muted">Dossier non trovato.</p>
      <Link to="/calcolatore" className="mt-4 inline-block px-4 py-2 bg-brand text-white rounded font-display uppercase tracking-widest text-sm">Torna al calcolatore</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Torna al calcolatore
      </button>

      <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
            <ClipboardList className="w-3 h-3" /> Check list documenti
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-ink" data-testid="checklist-title">
            Dossier <span className="text-brand">iperammortamento</span>
          </h1>
          <p className="mt-2 text-ink-muted text-sm font-mono">ID dossier: <span className="text-ink">{dossier.id}</span></p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button onClick={copyLink} data-testid="copy-link" className="inline-flex items-center gap-2 h-10 px-4 border border-line rounded text-sm hover:bg-line/40">
            <Copy className="w-4 h-4" /> Copia link
          </button>
          <p className="text-[11px] text-ink-muted max-w-xs text-right">Salva il link per riprendere il caricamento dei documenti in seguito.</p>
        </div>
      </div>

      {/* Tabs */}
      {dossier.sabatini && (
        <div className="mt-8 grid grid-cols-2 gap-3" data-testid="checklist-tabs">
          <button
            onClick={() => setActiveTab("iper")}
            data-testid="tab-iper"
            className={`p-5 rounded-lg border-2 text-left transition ${activeTab === "iper" ? "border-brand bg-brand/[0.06]" : "border-line bg-white hover:border-brand/40"}`}
          >
            <div className="flex items-center gap-2">
              <ClipboardList className={`w-4 h-4 ${activeTab === "iper" ? "text-brand" : "text-ink-muted"}`} />
              <span className="font-display text-[10px] tracking-[0.25em] uppercase text-ink-muted">Check list</span>
            </div>
            <div className="mt-2 font-display text-xl text-ink">Iperammortamento</div>
            <div className="mt-1 font-mono text-xs text-ink-muted">{iperCompleted}/{iperTotal} documenti</div>
          </button>
          <button
            onClick={() => setActiveTab("sabatini")}
            data-testid="tab-sabatini"
            className={`p-5 rounded-lg border-2 text-left transition ${activeTab === "sabatini" ? "border-brand bg-brand/[0.06]" : "border-line bg-white hover:border-brand/40"}`}
          >
            <div className="flex items-center gap-2">
              <Landmark className={`w-4 h-4 ${activeTab === "sabatini" ? "text-brand" : "text-ink-muted"}`} />
              <span className="font-display text-[10px] tracking-[0.25em] uppercase text-ink-muted">Check list</span>
            </div>
            <div className="mt-2 font-display text-xl text-ink">Nuova Sabatini</div>
            <div className="mt-1 font-mono text-xs text-ink-muted">{sabCompleted}/{sabTotal} documenti</div>
          </button>
        </div>
      )}

      {/* progress */}
      <div className="mt-8 bg-white border border-line rounded-lg p-6 shadow-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-xs tracking-[0.25em] uppercase text-ink-muted">Avanzamento</div>
            <div className="mt-1 font-display text-3xl text-ink" data-testid="progress-value">{completedItems}/{totalItems} voci · {totalDocs} file</div>
          </div>
          <div className="font-mono text-brand text-4xl font-semibold">{progress}%</div>
        </div>
        <div className="h-2 bg-line rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-brand"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* sections */}
      <div className="mt-8 space-y-8">
        {visibleSections.map((sec) => (
          <section key={sec.key} className={`bg-white border rounded-lg overflow-hidden shadow-card ${sec.key === "sabatini" ? "border-brand" : "border-line"}`} data-testid={`section-${sec.key}`}>
            <div className={`px-6 py-4 border-b ${sec.key === "sabatini" ? "bg-brand text-white border-brand" : "border-line bg-bg-dark text-white"}`}>
              <h2 className="font-display text-lg tracking-wide">{sec.title}</h2>
            </div>
            <div className="divide-y divide-line">
              {sec.items.map((item) => (
                <ChecklistItem
                  key={item.key}
                  dossierId={dossier.id}
                  item={item}
                  docs={docsByItem(item.key)}
                  onChange={load}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-8 text-xs text-ink-muted p-4 border border-line rounded bg-white/50">
        <strong>Nota:</strong> i file caricati sono privati al dossier corrente. Conserva il link per riaccedere. Dimensione massima per file: 20 MB.
      </div>
    </div>
  );
}

function ChecklistItem({ dossierId, item, docs, onChange }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const hasDoc = docs.length > 0;

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("item_key", item.key);
      await api.post(`/dossier/${dossierId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Caricato: ${file.name}`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = async (docId) => {
    if (!window.confirm("Rimuovere il documento?")) return;
    try {
      await api.delete(`/dossier/${dossierId}/doc/${docId}`);
      toast.success("Documento rimosso");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="px-6 py-5 flex flex-col lg:flex-row lg:items-start gap-4" data-testid={`item-${item.key}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {hasDoc ? (
          <CheckCircle2 className="w-5 h-5 mt-0.5 text-brand flex-shrink-0" />
        ) : (
          <Circle className="w-5 h-5 mt-0.5 text-ink-light flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className={`text-sm ${hasDoc ? "text-ink" : "text-ink-muted"}`}>{item.label}</div>
          {item.template === "modulo_raccolta_dati" && (
            <div className="mt-3 flex flex-wrap items-center gap-2 p-3 bg-brand/[0.05] border border-brand/30 rounded">
              <FileText className="w-4 h-4 text-brand flex-shrink-0" />
              <div className="text-xs text-ink flex-1 min-w-[200px]">
                <span className="font-semibold">Precompilato con i tuoi dati.</span>{" "}
                Scarica il modulo, completa i campi mancanti, firma e ricarica qui il file.
              </div>
              <a
                href={`${API}/dossier/${dossierId}/modulo-raccolta-dati`}
                data-testid={`download-template-${item.key}`}
                className="inline-flex items-center gap-2 h-8 px-3 bg-brand text-white rounded text-xs font-medium hover:bg-brand-dark"
              >
                <Download className="w-3.5 h-3.5" /> Scarica modulo (.pdf)
              </a>
            </div>
          )}
          {docs.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {docs.map((d) => (
                <li key={d.doc_id} className="flex items-center gap-2 text-xs" data-testid={`doc-${d.doc_id}`}>
                  <FileText className="w-3.5 h-3.5 text-ink-muted" />
                  <a
                    href={`${API}/dossier/${dossierId}/doc/${d.doc_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-ink hover:text-brand truncate max-w-xs"
                  >
                    {d.filename}
                  </a>
                  <span className="text-ink-muted font-mono">{(d.size / 1024).toFixed(0)} KB</span>
                  <button
                    onClick={() => onDelete(d.doc_id)}
                    data-testid={`delete-doc-${d.doc_id}`}
                    className="text-ink-muted hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <label className="lg:w-auto">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid={`upload-input-${item.key}`}
        />
        <span
          role="button"
          className={`inline-flex items-center gap-2 px-4 h-10 border rounded text-sm font-medium cursor-pointer transition ${
            uploading
              ? "border-brand text-brand"
              : hasDoc
              ? "border-line hover:bg-line/40 text-ink"
              : "border-brand bg-brand text-white hover:bg-brand-dark"
          }`}
          data-testid={`upload-btn-${item.key}`}
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Caricamento..." : hasDoc ? "Aggiungi altro" : "Carica"}
        </span>
      </label>
    </div>
  );
}
